import { useEffect, useState, type ReactNode } from 'react';
import Sidebar from '../components/Sidebar';
import {
  TrendingUp,
  ChevronRight,
  TrendingDown,
  AlertCircle,
  Clock,
  Banknote,
  CreditCard,
  Landmark,
  FileText,
  Globe,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getUserRole, getRoleDisplayName } from '../utils/rolePermissions';
import { apiCall, API_CONFIG } from '../config/api';
import { getProfitAndLoss } from '../utils/reportApi';
import type { ProfitAndLossReport } from '../types/report';
import { getOpenInvoices } from '../utils/invoiceApi';
import { getOpenBills } from '../utils/billApi';
import type { Invoice } from '../types/invoice';
import type { Bill } from '../types/bill';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { formatCurrency, formatCompactNumber } from '../utils/formatCurrency';
import { getDateRangeForPreset } from '../utils/dateRangePresets';

const PRIVACY_MASK = '••••••';
const PRIVACY_MASK_SHORT = '•••';

type CurrencyFormatOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

const PrivacyBlur = ({ hidden, children }: { hidden: boolean; children: ReactNode }) => {
  if (!hidden) return <>{children}</>;
  return (
    <div className="relative">
      <div className="blur-md select-none pointer-events-none">{children}</div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/50">
        <EyeOff className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type GpPeriod = 'today' | 'month' | 'quarter' | 'year' | 'week';

interface GpTrendPoint {
  period: string;
  start_date: string;
  end_date: string;
  gross_profit: string;
}

interface GpSummary {
  period: GpPeriod;
  current_start: string;
  current_end: string;
  previous_start: string;
  previous_end: string;
  gross_profit: string;
  previous_gross_profit: string;
  change_amount: string;
  change_percent: number | null;
  trend: GpTrendPoint[];
}

type PlPeriodKey = 'today' | 'month' | 'last_month' | 'quarter' | 'year';

interface PlPeriodOption {
  key: PlPeriodKey;
  label: string;
  subtitle: (now: Date) => string;
  current: (now: Date) => { start: string; end: string };
  previous: (now: Date) => { start: string; end: string };
}

const fmt = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PL_PERIODS: PlPeriodOption[] = [
  {
    key: 'today',
    label: 'Today',
    subtitle: (now) => `Net profit for ${now.toLocaleDateString('default', { month: 'long', day: 'numeric' })}`,
    current: (now) => ({ start: fmt(now), end: fmt(now) }),
    previous: (now) => {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      return { start: fmt(yesterday), end: fmt(yesterday) };
    },
  },
  {
    key: 'month',
    label: 'This month to date',
    subtitle: (now) => `Net profit for ${now.toLocaleString('default', { month: 'long' })} to date`,
    current: (now) => ({
      start: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
      end: fmt(now),
    }),
    previous: (now) => ({
      start: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      end: fmt(new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())),
    }),
  },
  {
    key: 'last_month',
    label: 'Last month',
    subtitle: (now) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return `Net profit for ${d.toLocaleString('default', { month: 'long' })}`;
    },
    current: (now) => ({
      start: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      end: fmt(new Date(now.getFullYear(), now.getMonth(), 0)),
    }),
    previous: (now) => ({
      start: fmt(new Date(now.getFullYear(), now.getMonth() - 2, 1)),
      end: fmt(new Date(now.getFullYear(), now.getMonth() - 1, 0)),
    }),
  },
  {
    key: 'quarter',
    label: 'This quarter',
    subtitle: () => 'Net profit for this quarter to date',
    current: (now) => {
      const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      return { start: fmt(qStart), end: fmt(now) };
    },
    previous: (now) => {
      const qStartMonth = Math.floor(now.getMonth() / 3) * 3;
      const pStart = new Date(now.getFullYear(), qStartMonth - 3, 1);
      const pEnd = new Date(now.getFullYear(), qStartMonth - 3 + Math.floor(now.getMonth() % 3) + (now.getDate() > 1 ? 0 : 0), now.getDate());
      return { start: fmt(pStart), end: fmt(pEnd) };
    },
  },
  {
    key: 'year',
    label: 'This year',
    subtitle: (now) => `Net profit for ${now.getFullYear()} to date`,
    current: (now) => ({
      start: fmt(new Date(now.getFullYear(), 0, 1)),
      end: fmt(now),
    }),
    previous: (now) => ({
      start: fmt(new Date(now.getFullYear() - 1, 0, 1)),
      end: fmt(new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())),
    }),
  },
];

interface PlCardData {
  current: ProfitAndLossReport;
  previous: ProfitAndLossReport | null;
}

type PmPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

const PM_PERIOD_OPTIONS: { value: PmPeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom' },
];

const defaultPmCustomRange = getDateRangeForPreset('this_month');

const CARD_BANK_CHARGE_RATE = 0.024;

const calcCardBankCharges = (cardAmount: number) =>
  Math.round(cardAmount * CARD_BANK_CHARGE_RATE * 100) / 100;

const buildPaymentMethodSummaryEndpoint = (
  period: PmPeriod,
  customStart: string,
  customEnd: string
): string => {
  if (period === 'custom') {
    const params = new URLSearchParams({
      start_date: customStart,
      end_date: customEnd,
    });
    return `${API_CONFIG.ENDPOINTS.REPORTS.PAYMENT_METHOD_SUMMARY}?${params}`;
  }
  return `${API_CONFIG.ENDPOINTS.REPORTS.PAYMENT_METHOD_SUMMARY}?period=${period}`;
};

type PaymentMethodKey = 'CASH' | 'CARD' | 'BANK_TRANSFER' | 'CHEQUE' | 'ONLINE';

interface PmMethodRow {
  payment_method: PaymentMethodKey;
  total: string;
  count: number;
}

interface PmSummary {
  period: PmPeriod;
  start_date: string;
  end_date: string;
  total: string;
  count: number;
  by_method: PmMethodRow[];
}

const PM_METHOD_META: Record<
  PaymentMethodKey,
  { label: string; color: string; bar: string; bg: string; icon: ReactNode }
> = {
  CASH: { label: 'Cash', color: 'text-emerald-600', bar: 'bg-emerald-500', bg: 'bg-emerald-50', icon: <Banknote className="h-4 w-4" /> },
  CARD: { label: 'Card', color: 'text-blue-600', bar: 'bg-blue-500', bg: 'bg-blue-50', icon: <CreditCard className="h-4 w-4" /> },
  BANK_TRANSFER: { label: 'Bank Transfer', color: 'text-violet-600', bar: 'bg-violet-500', bg: 'bg-violet-50', icon: <Landmark className="h-4 w-4" /> },
  CHEQUE: { label: 'Cheque', color: 'text-amber-600', bar: 'bg-amber-500', bg: 'bg-amber-50', icon: <FileText className="h-4 w-4" /> },
  ONLINE: { label: 'Online', color: 'text-cyan-600', bar: 'bg-cyan-500', bg: 'bg-cyan-50', icon: <Globe className="h-4 w-4" /> },
};

// ─── Main component ───────────────────────────────────────────────────────────

const Dashboard = () => {
  const navigate = useNavigate();
  const userRole = getUserRole();
  const roleDisplayName = getRoleDisplayName(userRole);

  const [gpData, setGpData] = useState<GpSummary | null>(null);
  const [gpPeriod, setGpPeriod] = useState<GpPeriod>('today');
  const [gpLoading, setGpLoading] = useState(true);
  const [gpError, setGpError] = useState<string | null>(null);

  const [plData, setPlData] = useState<PlCardData | null>(null);
  const [plPeriodKey, setPlPeriodKey] = useState<PlPeriodKey>('month');
  const [plLoading, setPlLoading] = useState(true);
  const [plError, setPlError] = useState<string | null>(null);
  const [plPeriodOpen, setPlPeriodOpen] = useState(false);

  const [overdueInvoices, setOverdueInvoices] = useState<Invoice[]>([]);
  const [pendingBills, setPendingBills] = useState<Bill[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);

  const [pmData, setPmData] = useState<PmSummary | null>(null);
  const [pmPeriod, setPmPeriod] = useState<PmPeriod>('today');
  const [pmCustomStart, setPmCustomStart] = useState(defaultPmCustomRange.startDate);
  const [pmCustomEnd, setPmCustomEnd] = useState(defaultPmCustomRange.endDate);
  const [pmLoading, setPmLoading] = useState(true);
  const [pmError, setPmError] = useState<string | null>(null);

  const [privacyHidden, setPrivacyHidden] = useState(
    () => localStorage.getItem('dashboard_privacy_hidden') === '1'
  );

  const togglePrivacy = () => {
    setPrivacyHidden(prev => {
      const next = !prev;
      localStorage.setItem('dashboard_privacy_hidden', next ? '1' : '0');
      return next;
    });
  };

  const showCurrency = (amount: string | number, options?: CurrencyFormatOptions) =>
    privacyHidden ? PRIVACY_MASK : formatCurrency(amount, options);

  const showCompact = (value: number) =>
    privacyHidden ? PRIVACY_MASK_SHORT : formatCompactNumber(value);

  const showPercent = (value: number, decimals = 0) =>
    privacyHidden ? PRIVACY_MASK_SHORT : `${Math.abs(value).toFixed(decimals)}%`;

  const now = new Date();
  const monthName = now.toLocaleString('default', { month: 'long' });
  const year = now.getFullYear();

  const fetchGrossProfitSummary = async (period: GpPeriod) => {
    setGpLoading(true);
    setGpError(null);
    try {
      const res = await apiCall(
        `${API_CONFIG.ENDPOINTS.REPORTS.GROSS_PROFIT_SUMMARY}?period=${period}`
      );
      setGpData(res.data);
    } catch {
      setGpError('Could not load gross profit data.');
    } finally {
      setGpLoading(false);
    }
  };

  useEffect(() => {
    fetchGrossProfitSummary(gpPeriod);
  }, [gpPeriod]);

  const fetchPL = async (key: PlPeriodKey) => {
    setPlLoading(true);
    setPlError(null);
    try {
      const opt = PL_PERIODS.find(p => p.key === key)!;
      const cur = opt.current(now);
      const prev = opt.previous(now);
      const [current, previous] = await Promise.all([
        getProfitAndLoss({ start_date: cur.start, end_date: cur.end }),
        getProfitAndLoss({ start_date: prev.start, end_date: prev.end }).catch(() => null),
      ]);
      setPlData({ current, previous });
    } catch {
      setPlError('Could not load profit & loss data.');
    } finally {
      setPlLoading(false);
    }
  };

  useEffect(() => {
    fetchPL(plPeriodKey);
  }, [plPeriodKey]);

  const fetchPaymentMethodSummary = async () => {
    if (pmPeriod === 'custom') {
      if (!pmCustomStart || !pmCustomEnd) {
        setPmLoading(false);
        setPmError(null);
        setPmData(null);
        return;
      }
      if (pmCustomStart > pmCustomEnd) {
        setPmLoading(false);
        setPmError('Start date must be on or before end date.');
        setPmData(null);
        return;
      }
    }

    setPmLoading(true);
    setPmError(null);
    try {
      const res = await apiCall(
        buildPaymentMethodSummaryEndpoint(pmPeriod, pmCustomStart, pmCustomEnd)
      );
      setPmData(res.data);
    } catch {
      setPmError('Could not load payment method data.');
      setPmData(null);
    } finally {
      setPmLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethodSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmPeriod, pmCustomStart, pmCustomEnd]);

  useEffect(() => {
    const fetchAlerts = async () => {
      setAlertsLoading(true);
      try {
        const [invoices, bills] = await Promise.all([
          getOpenInvoices({ include_customer: false }).catch(() => []),
          getOpenBills({ include_vendor: false }).catch(() => []),
        ]);

        setOverdueInvoices(invoices);
        setPendingBills(bills);
      } finally {
        setAlertsLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  return (
    <div className="h-screen bg-gray-50 flex w-full overflow-hidden">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-y-auto space-y-6 min-w-0">

          {/* ── Page header ─────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="mt-0.5 text-sm text-gray-500">
                {monthName} {year} — business overview
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={togglePrivacy}
                title={privacyHidden ? 'Show amounts' : 'Privacy mode'}
                aria-label={privacyHidden ? 'Show amounts' : 'Privacy mode'}
                aria-pressed={privacyHidden}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  privacyHidden
                    ? 'bg-slate-800 text-white border-slate-800 hover:bg-slate-700'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {privacyHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {privacyHidden ? 'Show' : 'Privacy'}
              </button>
              <span className="px-3 py-1.5 bg-slate-800 text-white rounded-full text-xs font-semibold tracking-wide uppercase">
                {roleDisplayName}
              </span>
            </div>
          </div>

          {/* ── Business Overview cards ───────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Gross Profit Card */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Card header */}
            <div className="px-5 pt-5 pb-4 border-b border-gray-50">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Gross Profit</p>
                  {gpLoading ? (
                    <div className="h-9 w-40 bg-gray-100 rounded-lg animate-pulse" />
                  ) : gpError ? (
                    <p className="text-sm text-red-400">{gpError}</p>
                  ) : (
                    <div className="flex items-end gap-3 flex-wrap">
                      <span className="text-3xl font-extrabold text-gray-900 leading-none">
                        {showCurrency(parseFloat(gpData?.gross_profit ?? '0'), { maximumFractionDigits: 0 })}
                      </span>
                      {gpData?.change_percent !== null && gpData?.change_percent !== undefined && (
                        <span
                          className={`flex items-center gap-1 text-sm font-semibold px-2 py-0.5 rounded-full mb-0.5 ${
                            (gpData.change_percent ?? 0) >= 0
                              ? 'bg-emerald-50 text-emerald-600'
                              : 'bg-red-50 text-red-500'
                          }`}
                        >
                          {(gpData.change_percent ?? 0) >= 0
                            ? <TrendingUp className="h-3.5 w-3.5" />
                            : <TrendingDown className="h-3.5 w-3.5" />}
                          {showPercent(gpData.change_percent ?? 0, 1)}
                        </span>
                      )}
                    </div>
                  )}
                  {!gpLoading && !gpError && gpData && (
                    <p className="text-xs text-gray-400 mt-1">
                      vs {showCurrency(parseFloat(gpData.previous_gross_profit), { maximumFractionDigits: 0 })} prev period
                      {' · '}
                      <span className={parseFloat(gpData.change_amount) >= 0 ? 'text-emerald-500' : 'text-red-400'}>
                        {!privacyHidden && parseFloat(gpData.change_amount) >= 0 ? '+' : ''}
                        {showCurrency(parseFloat(gpData.change_amount), { maximumFractionDigits: 0 })}
                      </span>
                    </p>
                  )}
                  {!gpLoading && !gpError && (
                    <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                      COGS for oversold items may update when related bills or adjustments are
                      received in a later period.
                    </p>
                  )}
                </div>

                {/* Period selector */}
                <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1">
                  {(['today', 'month', 'quarter', 'year', 'week'] as GpPeriod[]).map(p => (
                    <button
                      key={p}
                      onClick={() => setGpPeriod(p)}
                      disabled={gpLoading}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 capitalize ${
                        gpPeriod === p
                          ? 'bg-white text-gray-900 shadow-sm border border-gray-100'
                          : 'text-gray-400 hover:text-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Trend chart */}
            <div className="px-2 pt-3 pb-2">
              {gpLoading ? (
                <div className="h-32 bg-gray-50 rounded-lg animate-pulse mx-3 mb-2" />
              ) : gpError ? null : (
                <PrivacyBlur hidden={privacyHidden}>
                <ResponsiveContainer width="100%" height={130}>
                  <AreaChart
                    data={(gpData?.trend ?? []).map(t => ({
                      label: t.period,
                      gp: parseFloat(t.gross_profit),
                    }))}
                    margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="gpGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.18} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={v => showCompact(v)}
                      width={60}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-white border border-gray-100 shadow-lg rounded-lg px-3 py-2 text-sm">
                            <p className="font-semibold text-gray-600 text-xs mb-1">{label}</p>
                            <p className="font-bold text-emerald-600">
                              {showCurrency(payload[0].value as number, { maximumFractionDigits: 0 })}
                            </p>
                          </div>
                        );
                      }}
                      cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '4 2' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="gp"
                      name="Gross Profit"
                      stroke="#10b981"
                      strokeWidth={2.5}
                      fill="url(#gpGradient)"
                      dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                      activeDot={{ r: 5, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
                </PrivacyBlur>
              )}
              <p className="text-xs text-gray-300 text-right px-4 pb-1">Last 6 {gpPeriod}s</p>
            </div>
          </div>

          {/* ── Profit & Loss Card (QuickBooks-style) ────────────────────────── */}
          {(() => {
            const opt = PL_PERIODS.find(p => p.key === plPeriodKey)!;
            const income  = parseFloat(plData?.current.income.total  ?? '0');
            const expenses = parseFloat(plData?.current.expenses.total ?? '0');
            const netIncome = parseFloat(plData?.current.net_income ?? '0');
            const prevNet = plData?.previous ? parseFloat(plData.previous.net_income) : null;
            const changeAmt = prevNet !== null ? netIncome - prevNet : null;
            const changePct = (prevNet !== null && prevNet !== 0)
              ? ((netIncome - prevNet) / Math.abs(prevNet)) * 100
              : null;
            const marginPct = income !== 0
              ? Math.round((netIncome / income) * 100)
              : null;
            const maxBar = Math.max(income, expenses, 1);

            return (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-5 pt-5 pb-4 border-b border-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Profit &amp; Loss</p>

                    {/* Period dropdown */}
                    <div className="relative">
                      <button
                        onClick={() => setPlPeriodOpen(o => !o)}
                        disabled={plLoading}
                        className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-40"
                      >
                        {opt.label}
                        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${plPeriodOpen ? 'rotate-90' : 'rotate-90'}`} />
                      </button>
                      {plPeriodOpen && (
                        <div className="absolute right-0 top-6 z-20 bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[170px]">
                          {PL_PERIODS.map(p => (
                            <button
                              key={p.key}
                              onClick={() => { setPlPeriodKey(p.key); setPlPeriodOpen(false); }}
                              className={`w-full text-left px-4 py-2 text-xs font-medium transition-colors ${
                                plPeriodKey === p.key
                                  ? 'bg-blue-50 text-blue-600'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {plLoading ? (
                    <div className="mt-3 space-y-2">
                      <div className="h-4 w-44 bg-gray-100 rounded animate-pulse" />
                      <div className="h-9 w-36 bg-gray-100 rounded-lg animate-pulse" />
                      <div className="h-3 w-52 bg-gray-100 rounded animate-pulse" />
                    </div>
                  ) : plError ? (
                    <p className="mt-3 text-sm text-red-400">{plError}</p>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500 mt-2 mb-1">{opt.subtitle(now)}</p>
                      <div className="flex items-end gap-3 flex-wrap">
                        <span className="text-3xl font-extrabold text-gray-900 leading-none">
                          {showCurrency(netIncome, { maximumFractionDigits: 0 })}
                        </span>
                        {marginPct !== null && (
                          <span className="text-xs font-semibold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full mb-0.5">
                            {showPercent(marginPct, 0)}
                          </span>
                        )}
                      </div>
                      {changePct !== null && changeAmt !== null && (
                        <p className={`text-xs font-semibold mt-1.5 flex items-center gap-1 ${changePct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {changePct >= 0
                            ? <TrendingUp className="h-3.5 w-3.5" />
                            : <TrendingDown className="h-3.5 w-3.5" />}
                          {changePct >= 0 ? 'Up' : 'Down'} {showPercent(changePct, 0)}
                          <span className="font-normal text-gray-400 ml-1">from previous period</span>
                        </p>
                      )}
                    </>
                  )}
                </div>

                {/* Income / Expenses bars */}
                <div className="px-5 py-4 space-y-4">
                  {plLoading ? (
                    <>
                      <div className="space-y-1.5">
                        <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                        <div className="h-5 w-full bg-gray-100 rounded animate-pulse" />
                      </div>
                      <div className="space-y-1.5">
                        <div className="h-3 w-16 bg-gray-100 rounded animate-pulse" />
                        <div className="h-5 w-1/4 bg-gray-100 rounded animate-pulse" />
                      </div>
                    </>
                  ) : plError ? null : (
                    <>
                      {/* Income row */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-500">Income</span>
                          <span className="text-xs font-bold text-gray-800">
                            {showCurrency(income, { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                        <div className="h-4 w-full bg-gray-50 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-emerald-500 relative"
                            style={{ width: privacyHidden ? '0%' : `${Math.max((income / maxBar) * 100, income > 0 ? 2 : 0)}%` }}
                          >
                            {/* diagonal stripe accent at end */}
                            <div className="absolute right-0 top-0 h-full w-5 opacity-30"
                              style={{ background: 'repeating-linear-gradient(-45deg,transparent,transparent 2px,rgba(255,255,255,0.8) 2px,rgba(255,255,255,0.8) 4px)' }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Expenses row */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-500">Expenses</span>
                          <span className="text-xs font-bold text-gray-800">
                            {showCurrency(expenses, { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                        <div className="h-4 w-full bg-gray-50 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-orange-400"
                            style={{ width: privacyHidden ? '0%' : `${Math.max((expenses / maxBar) * 100, expenses > 0 ? 2 : 0)}%` }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between">
                  <button
                    onClick={() => navigate('/reports')}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    View full report
                  </button>
                  <span className="text-xs text-gray-300">
                    {plData?.current.start_date} – {plData?.current.end_date}
                  </span>
                </div>
              </div>
            );
          })()}

          </div>{/* end grid */}

          {/* ── Payments received by method ──────────────────────────────────── */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 pt-5 pb-4 border-b border-gray-50">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">
                    Payments Received
                  </p>
                  {pmLoading ? (
                    <div className="space-y-2 mt-2">
                      <div className="h-9 w-36 bg-gray-100 rounded-lg animate-pulse" />
                      <div className="h-3 w-28 bg-gray-100 rounded animate-pulse" />
                    </div>
                  ) : pmError ? (
                    <p className="text-sm text-red-400 mt-2">{pmError}</p>
                  ) : (
                    <>
                      <p className="text-3xl font-extrabold text-gray-900 leading-none mt-1">
                        {showCurrency(parseFloat(pmData?.total ?? '0'), { maximumFractionDigits: 0 })}
                      </p>
                      <p className="text-xs text-gray-400 mt-1.5">
                        {privacyHidden ? '••• payments' : `${pmData?.count ?? 0} payment${(pmData?.count ?? 0) !== 1 ? 's' : ''}`}
                        {pmData?.start_date === pmData?.end_date
                          ? ` · ${pmData?.start_date}`
                          : ` · ${pmData?.start_date} – ${pmData?.end_date}`}
                      </p>
                    </>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-1 bg-gray-50 rounded-lg p-1 flex-wrap justify-end">
                    {PM_PERIOD_OPTIONS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          if (value === 'custom' && (!pmCustomStart || !pmCustomEnd)) {
                            const range = getDateRangeForPreset('this_month');
                            setPmCustomStart(range.startDate);
                            setPmCustomEnd(range.endDate);
                          }
                          setPmPeriod(value);
                        }}
                        disabled={pmLoading}
                        className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-150 ${
                          pmPeriod === value
                            ? 'bg-white text-gray-900 shadow-sm border border-gray-100'
                            : 'text-gray-400 hover:text-gray-700'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {pmPeriod === 'custom' && (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <input
                        type="date"
                        value={pmCustomStart}
                        max={pmCustomEnd || undefined}
                        onChange={(e) => setPmCustomStart(e.target.value)}
                        disabled={pmLoading}
                        className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 disabled:bg-gray-50"
                        aria-label="Custom range start date"
                      />
                      <span className="text-xs text-gray-400">to</span>
                      <input
                        type="date"
                        value={pmCustomEnd}
                        min={pmCustomStart || undefined}
                        onChange={(e) => setPmCustomEnd(e.target.value)}
                        disabled={pmLoading}
                        className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 disabled:bg-gray-50"
                        aria-label="Custom range end date"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-5">
              {pmLoading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-24 bg-gray-50 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : pmError ? null : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {(pmData?.by_method ?? []).map(row => {
                    const meta = PM_METHOD_META[row.payment_method];
                    const amount = parseFloat(row.total);
                    const totalReceived = parseFloat(pmData?.total ?? '0');
                    const sharePct = totalReceived > 0 ? Math.round((amount / totalReceived) * 100) : 0;
                    const cardBankCharges =
                      row.payment_method === 'CARD' && amount > 0
                        ? calcCardBankCharges(amount)
                        : 0;

                    return (
                      <div
                        key={row.payment_method}
                        className={`rounded-xl border border-gray-100 p-4 flex flex-col gap-2 ${amount > 0 ? 'bg-white' : 'bg-gray-50/80'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className={`${meta.bg} ${meta.color} p-2 rounded-lg`}>
                            {meta.icon}
                          </div>
                          {amount > 0 && (
                            <span className="text-xs font-semibold text-gray-400">{showPercent(sharePct, 0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">{meta.label}</p>
                          <p className={`text-lg font-bold mt-0.5 ${amount > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                            {showCurrency(amount, { maximumFractionDigits: 0 })}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {privacyHidden ? '••• payments' : `${row.count} payment${row.count !== 1 ? 's' : ''}`}
                          </p>
                          {cardBankCharges > 0 && (
                            <p className="text-xs font-medium text-red-600 mt-1">
                              {privacyHidden
                                ? 'Bank charges •••'
                                : `Bank charges (2.4%): −${formatCurrency(cardBankCharges)}`}
                            </p>
                          )}
                        </div>
                        <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden mt-auto">
                          <div
                            className={`h-full rounded-full ${meta.bar}`}
                            style={{ width: privacyHidden ? '0%' : `${Math.max(sharePct, amount > 0 ? 4 : 0)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-50">
              <button
                onClick={() => navigate('/sales-transactions')}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
              >
                View payment transactions →
              </button>
            </div>
          </div>

          {/* ── Alert / action cards ─────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Open Invoices */}
            {(() => {
              const totalBalance = overdueInvoices.reduce((s, i) => s + parseFloat(i.balance), 0);
              const todayMs = new Date().setHours(0, 0, 0, 0);
              const daysUntilDue = (inv: Invoice) =>
                Math.floor((new Date(inv.due_date).getTime() - todayMs) / 86400000);

              return (
                <div className="bg-white rounded-xl border border-blue-100 shadow-sm overflow-hidden flex flex-col">
                  <div className="px-5 pt-5 pb-3 border-b border-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-50 p-2 rounded-lg">
                          <AlertCircle className="h-4 w-4 text-blue-500" />
                        </div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Open Invoices</p>
                      </div>
                      {alertsLoading ? null : (
                        <span className="text-xs font-bold bg-blue-50 text-blue-600 px-2.5 py-0.5 rounded-full">
                          {privacyHidden ? PRIVACY_MASK_SHORT : overdueInvoices.length}
                        </span>
                      )}
                    </div>
                    {alertsLoading ? (
                      <div className="space-y-2">
                        <div className="h-8 w-32 bg-gray-100 rounded animate-pulse" />
                        <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                      </div>
                    ) : (
                      <>
                        <p className="text-2xl font-extrabold text-gray-900">
                          {showCurrency(totalBalance, { maximumFractionDigits: 0 })}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">Total outstanding balance</p>
                      </>
                    )}
                  </div>

                  <div className="flex-1 px-5 py-3 space-y-2 overflow-hidden">
                    {alertsLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-9 bg-gray-50 rounded-lg animate-pulse" />
                      ))
                    ) : overdueInvoices.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">No open invoices</p>
                    ) : (
                      overdueInvoices.slice(0, 5).map(inv => {
                        const days = daysUntilDue(inv);
                        const isOverdue = days < 0;
                        return (
                          <div key={inv.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-800 truncate">
                                {inv.customer_snapshot?.name ?? `#${inv.invoice_number}`}
                              </p>
                              <p className={`text-xs font-medium ${isOverdue ? 'text-red-400' : days === 0 ? 'text-amber-500' : 'text-gray-400'}`}>
                                {isOverdue ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `Due in ${days}d`}
                                {' · '}{inv.invoice_number}
                              </p>
                            </div>
                            <span className="text-xs font-bold text-gray-800 shrink-0">
                              {showCurrency(parseFloat(inv.balance), { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="px-5 py-3 border-t border-gray-50">
                    <button
                      onClick={() => navigate('/sales')}
                      className="text-xs font-semibold text-blue-500 hover:text-blue-600 transition-colors"
                    >
                      View all invoices →
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Open Bills */}
            {(() => {
              const todayMs = new Date().setHours(0, 0, 0, 0);
              const totalBalance = pendingBills.reduce((s, b) => s + parseFloat(b.balance), 0);
              const daysUntilDue = (bill: Bill) =>
                Math.floor((new Date(bill.due_date).getTime() - todayMs) / 86400000);

              return (
                <div className="bg-white rounded-xl border border-orange-100 shadow-sm overflow-hidden flex flex-col">
                  <div className="px-5 pt-5 pb-3 border-b border-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="bg-orange-50 p-2 rounded-lg">
                          <Clock className="h-4 w-4 text-orange-500" />
                        </div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Open Bills</p>
                      </div>
                      {alertsLoading ? null : (
                        <span className="text-xs font-bold bg-orange-50 text-orange-600 px-2.5 py-0.5 rounded-full">
                          {privacyHidden ? PRIVACY_MASK_SHORT : pendingBills.length}
                        </span>
                      )}
                    </div>
                    {alertsLoading ? (
                      <div className="space-y-2">
                        <div className="h-8 w-32 bg-gray-100 rounded animate-pulse" />
                        <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />
                      </div>
                    ) : (
                      <>
                        <p className="text-2xl font-extrabold text-gray-900">
                          {showCurrency(totalBalance, { maximumFractionDigits: 0 })}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">Total outstanding balance</p>
                      </>
                    )}
                  </div>

                  <div className="flex-1 px-5 py-3 space-y-2 overflow-hidden">
                    {alertsLoading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="h-9 bg-gray-50 rounded-lg animate-pulse" />
                      ))
                    ) : pendingBills.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">No open bills</p>
                    ) : (
                      pendingBills.slice(0, 5).map(bill => {
                        const days = daysUntilDue(bill);
                        const isOverdue = days < 0;
                        return (
                          <div key={bill.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-gray-50 last:border-0">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-800 truncate">
                                {bill.vendor_snapshot?.name ?? `#${bill.bill_number}`}
                              </p>
                              <p className={`text-xs font-medium ${isOverdue ? 'text-red-400' : days === 0 ? 'text-amber-500' : 'text-gray-400'}`}>
                                {isOverdue ? `${Math.abs(days)}d overdue` : days === 0 ? 'Due today' : `Due in ${days}d`}
                                {' · '}{bill.bill_number}
                              </p>
                            </div>
                            <span className="text-xs font-bold text-gray-800 shrink-0">
                              {showCurrency(parseFloat(bill.balance), { maximumFractionDigits: 0 })}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="px-5 py-3 border-t border-gray-50">
                    <button
                      onClick={() => navigate('/purchases')}
                      className="text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors"
                    >
                      View all bills →
                    </button>
                  </div>
                </div>
              );
            })()}

          </div>{/* end alert grid */}

        </main>
    </div>
  );
};

export default Dashboard;
